import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

const NOPTemplate = ({children}) => <>{children}</>;
NOPTemplate.propTypes = {
    children: PropTypes.node
}

const DEFAULT_TOOLS = { slice: (data, ...args) => data.slice(...args), length: data => data.length };

const InfiniteScroll = ({ data, renderItem, batchSize = 20, style, Template = NOPTemplate, tools = DEFAULT_TOOLS, sentinelStyle,  ...props }) => {
    const [visibleItems, setVisibleItems] = useState([]);
    const observer = useRef(null);
    const loadingRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        setVisibleItems(currentItems => tools.slice(data, 0, Math.max(tools.length(currentItems), batchSize)));
    }, [data, batchSize, tools])

    // Intersection Observer callback
    const handleObserver = useCallback(entries => {
        const target = entries[0];
        if (target.isIntersecting) {
            setVisibleItems(items => {
                if (tools.length(items) >= tools.length(data)) {
                    return data;
                } else if (batchSize === Infinity) {
                    return data;
                } else {
                    return tools.slice(data, 0, tools.length(items) + batchSize);
                }
            })
        }
    }, [data, batchSize, tools]);

    // Setup and cleanup the observer
    useEffect(() => {
        if (loadingRef.current) {
            observer.current = new IntersectionObserver(handleObserver, {
                root: containerRef.current, // assuming viewport as root
                rootMargin: '10px',
                threshold: 0.1
            });
            observer.current.observe(loadingRef.current);
        }

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [loadingRef, handleObserver, data, renderItem, sentinelStyle]);

    useEffect(() => {
        const checkSentinelVisibility = () => {
            if (!loadingRef.current || !containerRef.current) return;

            const sentinelRect = loadingRef.current.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();

            if (sentinelRect.top < containerRect.bottom && sentinelRect.bottom > containerRect.top) {
                handleObserver([{ isIntersecting: true }]);
            }
        };

        // Run the check a short time after mount to ensure layout is settled
        const timeoutId = setTimeout(checkSentinelVisibility, 150);

        return () => clearTimeout(timeoutId);
    }, [visibleItems, handleObserver]);

    return (
        <div ref={containerRef} style={{ overflowY: 'auto', height: '100%', ...style }} {...props}>
            <Template>
            {visibleItems.map(renderItem)}
            {tools.length(visibleItems) < tools.length(data) && (
                <div style={{ display: 'block', clear: 'both', ...sentinelStyle }} ref={loadingRef}>&nbsp;</div>
            )}
            </Template>
        </div>
    );
}

InfiniteScroll.propTypes = {
    data: PropTypes.array,
    renderItem: PropTypes.func,
    batchSize: PropTypes.number,
    style: PropTypes.object,
    Template: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    tools: PropTypes.shape({ length: PropTypes.func.isRequired, slice: PropTypes.func.isRequired })
}

export default InfiniteScroll;
